// Angular
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';

// Models
import { AppCleanup, DrawerModel, ToasterModel } from '../models';
import { JobCommentDefaultGroupLocalStorage } from '../../modules/comment/models/job-comment-default-group-dto';
import { BopInspectionObject } from '../../modules/job/components/bop-inspection/bop-inspection.component';
import { UserDashboardSettings } from '../../modules/channel/models/user-dashboard-settings.model';

// Store
import { StorageActions } from './storage.action';

export interface StorageStateModel {
	tenantId: number;
	commentGroups: JobCommentDefaultGroupLocalStorage[];
	commentFontSize: string;
	commentDrawerSettings: DrawerModel;
	modelDrawerSettings: DrawerModel;
	selectedFilterJobTypeIds: number[];
	toastHistory: ToasterModel[];
	language: string;
	bopInspection: BopInspectionObject[];
	sideNav: SideNavModel;
	previousRoute: string;
	currentRoute: string;
	currentDashboardId: number;
	userDashboardSettings: UserDashboardSettings;
	darkMode: boolean;
	brightness: string;
	jobRouteParams: Params; // Storing this here because these params are not accessible everywhere where needed
	deviceTypes: any;
}

const defaultState: StorageStateModel = {
	tenantId: undefined,
	commentGroups: undefined,
	commentFontSize: undefined,
	commentDrawerSettings: undefined,
	modelDrawerSettings: undefined,
	selectedFilterJobTypeIds: undefined,
	toastHistory: undefined,
	language: undefined,
	bopInspection: undefined,
	sideNav: undefined,
	previousRoute: undefined,
	currentRoute: undefined,
	currentDashboardId: undefined,
	userDashboardSettings: undefined,
	darkMode: undefined,
	brightness: undefined,
	jobRouteParams: undefined,
	deviceTypes: undefined,
};

@State<any>({
	name: 'storage',
	defaults: defaultState,
})
@Injectable()
export class StorageState extends AppCleanup {
	constructor() {
		super();
	}
	
	@Action(StorageActions.SetTenantId)
	setTenantId(ctx: StateContext<StorageStateModel>, action: StorageActions.SetTenantId) {
		ctx.patchState({
			tenantId: action.tenantId,
		});
	}
	
	@Action(StorageActions.SetCommentGroups)
	setCommentGroups(ctx: StateContext<StorageStateModel>, action: StorageActions.SetCommentGroups) {
		ctx.patchState({
			commentGroups: action.defaultCommentGroups,
		});
	}
	
	@Action(StorageActions.SetCommentFontSize)
	setCommentFontSize(ctx: StateContext<StorageStateModel>, action: StorageActions.SetCommentFontSize) {
		ctx.patchState({
			commentFontSize: action.fontSize,
		});
	}
	
	@Action(StorageActions.SetCommentDrawerSettings)
	setCommentDrawer(ctx: StateContext<StorageStateModel>, action: StorageActions.SetCommentDrawerSettings) {
		ctx.patchState({
			commentDrawerSettings: action.commentDrawerSettings,
		});
	}
	
	@Action(StorageActions.SetModelDrawerSettings)
	setModelDrawer(ctx: StateContext<StorageStateModel>, action: StorageActions.SetModelDrawerSettings) {
		ctx.patchState({
			modelDrawerSettings: action.modelDrawerSettings,
		});
	}
	
	@Action(StorageActions.SetSelectedFilterJobTypeIds)
	setSelectedFilterJobTypeIds(ctx: StateContext<StorageStateModel>, action: StorageActions.SetSelectedFilterJobTypeIds) {
		ctx.patchState({
			selectedFilterJobTypeIds: action.jobTypeIds,
		});
	}
	
	@Action(StorageActions.SetToastHistory)
	setToastHistory(ctx: StateContext<StorageStateModel>, action: StorageActions.SetToastHistory) {
		ctx.patchState({
			toastHistory: action.toastHistory,
		});
	}
	
	@Action(StorageActions.ClearToastHistory)
	clearToastHistory(ctx: StateContext<StorageStateModel>) {
		ctx.patchState({
			toastHistory: [],
		});
	}
	
	@Action(StorageActions.SetLanguage)
	setLanguage(ctx: StateContext<StorageStateModel>, action: StorageActions.SetLanguage) {
		ctx.patchState({
			language: action.language,
		});
	}
	
	@Action(StorageActions.SetCurrentRoute)
	setCurrentRoute(ctx: StateContext<StorageStateModel>, action: StorageActions.SetCurrentRoute) {
		let previousRoute = ctx.getState().currentRoute;
		let currentRoute = action.currentRoute;
		
		ctx.patchState({
			previousRoute: currentRoute === previousRoute ? undefined : previousRoute,
			currentRoute: currentRoute,
		});
	}
	
	@Action(StorageActions.SetJobRouteParams)
	setJobRouteParams(ctx: StateContext<StorageStateModel>, action: StorageActions.SetJobRouteParams) {
		ctx.patchState({
			jobRouteParams: action.params,
		});
	}
	
	@Action(StorageActions.SetCurrentDashboardId)
	setCurrentDashboardId(ctx: StateContext<StorageStateModel>, action: StorageActions.SetCurrentDashboardId) {
		ctx.patchState({
			currentDashboardId: action.id,
		});
	}
	
	@Action(StorageActions.SetUserDashboardSettings)
	setUserDashboardSettings(ctx: StateContext<StorageStateModel>, action: StorageActions.SetUserDashboardSettings) {
		ctx.patchState({
			userDashboardSettings: action.settings,
		});
	}
	
	@Action(StorageActions.SetSideNav)
	setSideNav(ctx: StateContext<StorageStateModel>, action: StorageActions.SetSideNav) {
		ctx.patchState({
			sideNav: action.sideNav,
		});
	}
	
	@Action(StorageActions.AddBopReport)
	addBopReport(ctx: StateContext<StorageStateModel>, action: StorageActions.AddBopReport) {
		ctx.setState(
			patch({
				bopInspection: append([action.bopReportObject]),
			})
		);
	}
	
	@Action(StorageActions.SetDarkMode)
	setDarkMode(ctx: StateContext<StorageStateModel>, action: StorageActions.SetDarkMode) {
		ctx.patchState({
			darkMode: action.darkMode,
		});
	}
	
	@Action(StorageActions.SetAppBrightness)
	setAppBrightness(ctx: StateContext<StorageStateModel>, action: StorageActions.SetAppBrightness) {
		ctx.patchState({
			brightness: action.brightness,
		});
	}
	
	@Action(StorageActions.SetDeviceTypes)
	setDeviceTypes(ctx: StateContext<StorageStateModel>, action: StorageActions.SetDeviceTypes) {
		ctx.patchState({
			deviceTypes: action.types,
		});
	}
	
	@Selector()
	static tenantId(state: StorageStateModel): number {
		return state.tenantId;
	}
	
	@Selector()
	static commentGroups(state: StorageStateModel): JobCommentDefaultGroupLocalStorage[] {
		return state.commentGroups;
	}
	
	@Selector()
	static commentFontSize(state: StorageStateModel): string {
		return state.commentFontSize;
	}
	
	@Selector()
	static commentDrawerSettings(state: StorageStateModel): DrawerModel {
		return state.commentDrawerSettings;
	}
	
	@Selector()
	static modelDrawerSettings(state: StorageStateModel): DrawerModel {
		return state.modelDrawerSettings;
	}
	
	@Selector()
	static selectedFilterJobTypeIds(state: StorageStateModel): number[] {
		return state.selectedFilterJobTypeIds;
	}
	
	@Selector()
	static toastHistory(state: StorageStateModel): ToasterModel[] {
		return state.toastHistory;
	}
	
	@Selector()
	static language(state: StorageStateModel): string {
		return state.language;
	}
	
	@Selector()
	static bopInspections(state: StorageStateModel): BopInspectionObject[] {
		return state.bopInspection;
	}
	
	@Selector()
	static sideNav(state: StorageStateModel): SideNavModel {
		return state.sideNav;
	}
	
	@Selector()
	static previousRoute(state: StorageStateModel): string {
		return state.previousRoute;
	}
	
	@Selector()
	static currentRoute(state: StorageStateModel): string {
		return state.currentRoute;
	}
	
	@Selector()
	static jobRouteParams(state: StorageStateModel): Params {
		return state.jobRouteParams;
	}
	
	@Selector()
	static currentDashboardId(state: StorageStateModel): number {
		return state.currentDashboardId;
	}
	
	@Selector()
	static userDashboardSettings(state: StorageStateModel): UserDashboardSettings {
		return state.userDashboardSettings;
	}
	
	@Selector()
	static darkMode(state: StorageStateModel): boolean {
		return state.darkMode;
	}
	
	@Selector()
	static brightness(state: StorageStateModel): string {
		return state.brightness;
	}
	
	@Selector()
	static deviceTypes(state: StorageStateModel): any {
		return state.deviceTypes;
	}
}

export class SideNavModel {
	opened: boolean;
	collapsed: boolean;
	pinned: boolean;
}